import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { AppTopbar } from "@/components/layout/AppTopbar";
import { Sidebar } from "@/components/layout/Sidebar";
import { VideoPlayerLayout, VideoPlayerLayoutSkeleton } from "@/components/VideoPlayerLayout";
import { useCourseIdParam } from "@/hooks/useUrlParams";
import { useCourseItem } from "@/hooks/useCourses";
import { useVideos, useCourseVideos, useVideoIndex } from "@/hooks/useVideos";
import { useCoursePermissions, hasPermission } from "@/hooks/usePermissions";
import { useCreateCourseShare } from "@/hooks/useSharing";
import { useAuth } from "@/lib/auth";
import { useAppStore } from "@/lib/store";
import { buildShareTutorialUrl, copyToClipboard, formatDuration } from "@/lib/helpers";
import { Check, Clock, Film, Plus, Share2 } from "lucide-react";

export function CoursePage() {
  const courseId = useCourseIdParam();
  const { user } = useAuth();
  const { data: course, loading: courseLoading } = useCourseItem(courseId);
  const { data: allVideos, loading: videosLoading } = useVideos();
  const { data: videoIndex } = useVideoIndex();
  const { data: permissions, loading: permissionsLoading } = useCoursePermissions();
  const { execute: createShare } = useCreateCourseShare();
  const { openModal, setSelectedAddToPlaylistCourseId, dashboardSearchInput, setDashboardSearchInput } = useAppStore();

  const courseVideos = useCourseVideos(allVideos, courseId);
  const [copiedShare, setCopiedShare] = useState(false);

  // Check permission
  const hasAccess = useMemo(() => {
    if (!user || !permissions || !courseId) return false;
    return hasPermission(permissions, courseId, user.id);
  }, [user, permissions, courseId]);

  const handleShare = async () => {
    if (!courseId || !user) return;

    // Always go through the mutation so cached share data cannot copy a
    // stale/deleted token and existing links get their expiry refreshed.
    const result = await createShare(courseId);
    if (result.data?.share_token) {
      await copyToClipboard(buildShareTutorialUrl(result.data.share_token));
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2000);
    }
  };

  const handleSaveToPerformance = () => {
    if (!courseId) return;
    setSelectedAddToPlaylistCourseId(courseId);
    openModal("save-to-performance");
  };

  const loading = courseLoading || videosLoading || permissionsLoading;
  const courseName = course?.name ?? "Course";
  const instructorName = (course as any)?.Instructor?.name ?? null;
  const categories = (course as any)?.category ?? [];
  const duration = course?.total_duration ? formatDuration(course.total_duration) : null;
  const playableVideoCount = courseVideos.length;

  // Shared outer shell: Sidebar + AppTopbar
  const shell = (content: React.ReactNode) => (
    <div className="flex min-h-screen flex-col bg-[#f6f5f6] text-[#1c171b] min-[992px]:flex-row">
      <Sidebar />
      <div className="min-w-0 flex-1">
        <AppTopbar
          title={courseName}
          backTo="/app/dashboard/home"
          searchValue={dashboardSearchInput}
          onSearchChange={setDashboardSearchInput}
        />
        {content}
      </div>
    </div>
  );

  if (loading) {
    return shell(
      <main className="flex flex-1 flex-col">
        <VideoPlayerLayoutSkeleton />
      </main>
    );
  }

  if (!course) {
    return shell(
      <main className="flex flex-1 items-center justify-center p-8">
        <div className="text-center">
          <p className="text-neutral-dark">This course could not be found.</p>
          <Button asChild className="mt-4 rounded-round bg-pink-darker text-white hover:bg-pink-dark">
            <Link to="/app/dashboard/home">Back to Dashboard</Link>
          </Button>
        </div>
      </main>
    );
  }

  if (!hasAccess) {
    return shell(
      <main className="flex flex-1 items-center justify-center p-8">
        <div className="text-center">
          <p className="mb-2 font-display text-2xl text-pink-darker">Access Required</p>
          <p className="text-neutral-dark">
            You need to purchase this course to watch the videos.
          </p>
          <Button asChild className="mt-4 rounded-round bg-pink-darker text-white hover:bg-pink-dark">
            <Link to={`/app/tutorial/${courseId}`}>View Tutorial Details</Link>
          </Button>
        </div>
      </main>
    );
  }

  return shell(
    <main className="flex flex-1 flex-col">
      <VideoPlayerLayout
        courseVideos={courseVideos}
        videoIndex={videoIndex}
        instructorName={instructorName ?? "Khusbu Gemmati"}
        actionButtons={
          <>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-[0.6rem] border border-[#e2e4e9] bg-white px-4 py-2 text-[0.82rem] font-medium text-[#1c171b] transition-colors hover:bg-[#f6f5f6]"
              onClick={handleSaveToPerformance}
            >
              <Plus className="h-3.5 w-3.5" />
              Save to performance
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-[0.6rem] bg-[#762a5c] px-4 py-2 text-[0.82rem] font-medium text-white transition-colors hover:bg-[#5e2249]"
              onClick={handleShare}
            >
              {copiedShare ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  Copied!
                </>
              ) : (
                <>
                  <Share2 className="h-3.5 w-3.5" />
                  Share Tutorial
                </>
              )}
            </button>
          </>
        }
      />

      {/* Tutorial info */}
      <div className="px-[clamp(1rem,3vw,3rem)] pb-10">
        <div className="rounded-xl border border-[#e2e4e9] bg-white p-5">
          <h2 className="mb-1 font-display text-xl text-pink-darker">{course.name}</h2>

          {/* Stats row */}
          <div className="mb-3 flex flex-wrap items-center gap-4 text-sm text-[#6b5e67]">
            {duration && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {duration} total
              </span>
            )}
            {playableVideoCount > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Film className="h-3.5 w-3.5" />
                {playableVideoCount} video{playableVideoCount === 1 ? "" : "s"}
              </span>
            )}
          </div>

          {/* Categories */}
          {categories.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {categories.map((cat: { name: string }) => (
                <span
                  key={cat.name}
                  className="rounded-round bg-[#f6f5f6] px-2.5 py-1 text-xs text-[#6b5e67]"
                >
                  {cat.name}
                </span>
              ))}
            </div>
          )}

          {/* Short description */}
          {course.short_description && (
            <p className="text-sm leading-relaxed text-[#4a3f46]">{course.short_description}</p>
          )}

          {/* Long description */}
          {course.long_description && (
            <div className="mt-4 border-t border-[#e2e4e9] pt-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#b0a8ad]">
                About this tutorial
              </h3>
              <div
                className="text-sm leading-relaxed text-[#4a3f46] [&_a]:text-blue-brand [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-blue-brand/80"
                dangerouslySetInnerHTML={{ __html: course.long_description }}
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
